import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import { followUpOf } from './landingAgentSlice';
import {
  listDefinitions,
  requestDefinition,
  proposeDefinition,
  reviseDefinition,
  saveDefinition,
  publishDefinition,
  requestDefinitionChanges,
  deleteDefinition,
} from '../../apis';
import type {
  DefinitionRow,
  DefinitionProposal,
  DefinitionCandidate,
  DefinitionValidation,
} from '../../apis';

export type DefinitionStatusFilter = 'All' | 'Pending review' | 'Published' | 'Needs changes' | 'Requested';

/**
 * What the agent asked for when it hit an undefined term. Mirrors the
 * `definition_required` WebSocket event; one lives per chat turn, so an older
 * turn's capture card keeps its own state when a new question is asked.
 */
export interface DefinitionRequest {
  sub_task_id: string;
  description?: string;
  term: string;
  kind_hint?: string;
  /** Null for a term asked back under Auto (#1665) until the asker chooses one of `perspective_options`. */
  perspective: string | null;
  perspective_name?: string | null;
  /** #1665: the perspectives the draft may belong to, in catalog order — offered only while `perspective` is null. */
  perspective_options?: { id: string; name: string }[];
  /** #1665: catalog metrics that might be what the asker means — each a one-off re-ask, never a definition. */
  meanings?: { id: string; label: string }[];
  /** #1665: the question back, in order — words, or a cited fact (the term, a meaning, "in your own words"). */
  question?: { text?: string; cite?: string; kind?: string; label?: string }[];
  /** #1665: the asker's own words for the term, kept from a reply until a perspective is chosen. */
  english?: string | null;
  /** #1665: the reply drafted and run (nothing saved): shown as the proposal, its Save answers `asked_question`. */
  proposal?: DefinitionProposal | null;
  error?: string | null;
  /** #1665: the question the term was asked in, re-asked once the definition is saved. */
  asked_question?: string | null;
  term_check?: { session_id: string | null; stage: 'ask' | 'where' | 'proposal' };
  /** #1670: where the stated meaning was drafted, read for the viewer now — the home and the near meanings that
   *  placed it there; absent when it no longer opens for them. */
  placement?: { perspective: string; perspective_name: string; meanings: { id: string; label: string }[];
    by: 'shared_home' | 'asker_words' } | null;
  /** #1670: the other perspectives the meaning may be drafted in, computed for the viewer (never stored). */
  alternatives?: { id: string; name: string }[];
  kinds?: string[];
  field_groups?: { name: string; fields: { name: string; type?: string; table?: string; description?: string }[] }[];
  field_locations?: { perspective: string; perspective_name: string; field: string }[];
  nearby?: { name: string; display_name?: string; description?: string }[];
  nearest?: string[];
  /**
   * Somebody has already defined this term and it is waiting on review. Not
   * resolving yet — that's the point of the gate — but worth saying before the
   * asker writes a second definition of the same thing.
   */
  pending_drafts?: PendingDraft[];
  /** The dimension the question asked to slice by, so the preview matches. */
  breakdown?: string | null;
  date_field?: string | null;
}

/** An unreviewed definition of the term the asker just used. */
export interface PendingDraft {
  id: string;
  name: string;
  display_name?: string;
  english?: string;
  formula?: string;
  status?: string;
  author?: string;
  review_note?: string | null;
  /** The revision this card is showing — sent back so consent binds to the
   *  wording that was read, not to the id. */
  version?: string;
}

export interface DefinitionsSlice {
  definitions: DefinitionRow[];
  definitionsLoading: boolean;
  definitionsError: string | null;
  definitionsFilter: DefinitionStatusFilter;
  definitionsSearch: string;
  selectedDefinitionId: string | null;

  // Per-turn capture state, keyed by turn id. Keeping it out of the turn
  // object itself means an in-flight proposal survives the turn list being
  // replaced wholesale on every event.
  definitionProposals: Record<string, DefinitionProposal | null>;
  definitionProposing: Record<string, boolean>;
  definitionCaptureError: Record<string, string | null>;
  definitionSaved: Record<string, DefinitionRow | null>;
  definitionRequestMessage: Record<string, string>;

  fetchDefinitions: () => Promise<void>;
  setDefinitionsFilter: (f: DefinitionStatusFilter) => void;
  setDefinitionsSearch: (q: string) => void;
  selectDefinition: (id: string | null) => void;

  proposeTurnDefinition: (turnId: string, body: {
    term: string; perspective: string; kind?: string;
    english?: string; blocks?: any; sql?: string;
    breakdown?: string;
    provenance?: Record<string, any>;
  }) => Promise<void>;
  requestTurnDefinition: (turnId: string, body: Parameters<typeof requestDefinition>[0]) => Promise<void>;
  clearTurnProposal: (turnId: string) => void;
  /** A proposal the server already made for this turn (#1665: a reply drafted and run), unless one is held. */
  seedTurnProposal: (turnId: string, proposal: DefinitionProposal) => void;
  /** `scope` is the re-ask's binding to its answer (`followUpOf(turn, { sameQuestion: true })`), passed by a card
   *  whose turn the store does not hold (a document's number, #763); else the turn is looked up by id. */
  approveTurnDefinition: (turnId: string, question: string, scope?: ReturnType<typeof followUpOf>) => Promise<void>;

  /** Re-propose a flagged definition; returns the preview, saves nothing. */
  reviseDefinitionById: (id: string, body: { english?: string; sql?: string })
    => Promise<DefinitionProposal | null>;
  /** Save a revision — clears the needs_changes flag back to draft. */
  resubmitDefinition: (candidate: DefinitionCandidate) => Promise<boolean>;
  publishDefinitionById: (id: string) => Promise<void>;
  requestDefinitionChangesById: (id: string, note: string) => Promise<void>;
  deleteDefinitionById: (id: string) => Promise<void>;
}

function errText(e: any): string {
  return (e?.response?.data?.error || e?.message || 'Something went wrong').toString();
}

export const createDefinitionsSlice: StateCreator<
  VoyagerState, [], [], DefinitionsSlice
> = (set, get) => ({
  definitions: [],
  definitionsLoading: false,
  definitionsError: null,
  definitionsFilter: 'All',
  definitionsSearch: '',
  selectedDefinitionId: null,
  definitionProposals: {},
  definitionProposing: {},
  definitionCaptureError: {},
  definitionSaved: {},
  definitionRequestMessage: {},

  fetchDefinitions: async () => {
    set({ definitionsLoading: true, definitionsError: null });
    try {
      set({ definitions: await listDefinitions(), definitionsLoading: false });
    } catch (e) {
      set({ definitionsError: errText(e), definitionsLoading: false });
    }
  },

  setDefinitionsFilter: (f) => set({ definitionsFilter: f }),
  setDefinitionsSearch: (q) => set({ definitionsSearch: q }),
  selectDefinition: (id) => set((s) => ({
    selectedDefinitionId: s.selectedDefinitionId === id ? null : id,
  })),

  proposeTurnDefinition: async (turnId, body) => {
    set((s) => ({
      definitionProposing: { ...s.definitionProposing, [turnId]: true },
      definitionCaptureError: { ...s.definitionCaptureError, [turnId]: null },
    }));
    try {
      const proposal = await proposeDefinition(body);
      set((s) => ({
        definitionProposals: { ...s.definitionProposals, [turnId]: proposal },
        definitionProposing: { ...s.definitionProposing, [turnId]: false },
      }));
    } catch (e) {
      set((s) => ({
        definitionProposing: { ...s.definitionProposing, [turnId]: false },
        definitionCaptureError: { ...s.definitionCaptureError, [turnId]: errText(e) },
      }));
    }
  },

  requestTurnDefinition: async (turnId, body) => {
    set((s) => ({ definitionProposing: { ...s.definitionProposing, [turnId]: true },
      definitionCaptureError: { ...s.definitionCaptureError, [turnId]: null } }));
    try {
      const result = await requestDefinition(body);
      set((s) => ({ definitionSaved: { ...s.definitionSaved, [turnId]: result.definition },
        definitionRequestMessage: { ...s.definitionRequestMessage, [turnId]: result.message },
        definitions: [result.definition, ...s.definitions.filter((row) => row.id !== result.definition.id)] }));
      // sent for review: the card no longer takes an answer (#1503)
      get().resolveWait(turnId);
    } catch (e) {
      set((s) => ({ definitionCaptureError: { ...s.definitionCaptureError, [turnId]: errText(e) } }));
    } finally {
      set((s) => ({ definitionProposing: { ...s.definitionProposing, [turnId]: false } }));
    }
  },

  seedTurnProposal: (turnId, proposal) => set((s) => (turnId in s.definitionProposals ? {} : {
    definitionProposals: { ...s.definitionProposals, [turnId]: proposal },
  })),

  clearTurnProposal: (turnId) => set((s) => ({
    definitionProposals: { ...s.definitionProposals, [turnId]: null },
    definitionCaptureError: { ...s.definitionCaptureError, [turnId]: null },
  })),

  approveTurnDefinition: async (turnId, question, scope) => {
    const proposal = get().definitionProposals[turnId];
    if (!proposal?.candidate) return;
    set((s) => ({
      definitionProposing: { ...s.definitionProposing, [turnId]: true },
      definitionCaptureError: { ...s.definitionCaptureError, [turnId]: null },
    }));
    try {
      const saved = await saveDefinition(
        proposal.candidate as DefinitionCandidate,
        proposal.validation as DefinitionValidation | null,
        // The card showed the "returned no rows" warning above this button,
        // so approving it is a seen decision.
        !!proposal.validation?.ok && !(proposal.validation?.rows || []).length,
      );
      set((s) => ({
        definitionSaved: { ...s.definitionSaved, [turnId]: saved },
        definitionProposing: { ...s.definitionProposing, [turnId]: false },
        definitions: [saved, ...s.definitions.filter((d) => d.id !== saved.id)],
      }));
      // saved: the card no longer takes an answer, whether or not the re-ask below starts (#1503)
      get().resolveWait(turnId);
      // The term now resolves, so the question that stalled on it is
      // answerable. Re-asking it is the point of having captured the
      // definition — making the user retype it would be the bug.
      // asked again in the perspective and scope it stalled in (#767), never the thread's pick
      if (question) get().sendLandingAgentQuery(question, scope ?? followUpOf(get().landingAgentTurns.find((t) => t.id === turnId), { sameQuestion: true }));
    } catch (e) {
      set((s) => ({
        definitionProposing: { ...s.definitionProposing, [turnId]: false },
        definitionCaptureError: { ...s.definitionCaptureError, [turnId]: errText(e) },
      }));
    }
  },

  reviseDefinitionById: async (id, body) => {
    set({ definitionsError: null });
    try {
      return await reviseDefinition(id, body);
    } catch (e) {
      set({ definitionsError: errText(e) });
      return null;
    }
  },

  resubmitDefinition: async (candidate) => {
    try {
      const saved = await saveDefinition(candidate, null, true);
      set((s) => ({
        definitions: s.definitions.map((d) => (d.id === saved.id ? { ...d, ...saved } : d)),
      }));
      return true;
    } catch (e) {
      set({ definitionsError: errText(e) });
      return false;
    }
  },

  publishDefinitionById: async (id) => {
    try {
      // Send the version we rendered, so approval binds to what was on screen.
      const shown = get().definitions.find((d) => d.id === id);
      const row = await publishDefinition(id, '', shown?.updated_at);
      set((s) => ({ definitions: s.definitions.map((d) => (d.id === id ? { ...d, ...row } : d)) }));
      await get().fetchDefinitions();
    } catch (e) {
      set({ definitionsError: errText(e) });
    }
  },

  requestDefinitionChangesById: async (id, note) => {
    try {
      const row = await requestDefinitionChanges(id, note);
      set((s) => ({ definitions: s.definitions.map((d) => (d.id === id ? { ...d, ...row } : d)) }));
    } catch (e) {
      set({ definitionsError: errText(e) });
    }
  },

  deleteDefinitionById: async (id) => {
    try {
      await deleteDefinition(id);
      set((s) => ({
        definitions: s.definitions.filter((d) => d.id !== id),
        selectedDefinitionId: s.selectedDefinitionId === id ? null : s.selectedDefinitionId,
      }));
    } catch (e) {
      set({ definitionsError: errText(e) });
    }
  },
});
